import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import StringUtils from "@/utils/StringUtils";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import organizationClient from "@apis/organizationClient";
import { useOrg } from "@/contexts/OrgContext";
import { RootState } from "@/types/RootState";

interface CheckPagePermissionResponse {
  orgName?: string;
  requiredFeatures?: string;
  currentPlan?: string;
}

export interface PagePermissionCheck {
  /** false only when the selected org lacks the page's required feature(s) */
  hasAccess: boolean;
  orgName?: string;
  requiredFeatures: string[];
  /** Display name of the org's base plan; absent when it resolves to no plan */
  currentPlan?: string;
  scopeOrgId: number | null;
  /**
   * The same sentence `PagePermissionWarning` renders, as plain text, for a
   * tooltip on a disabled action. Empty while access holds. Built from the
   * placeholder keys so word order stays translatable (STIC-15987).
   */
  noAccessMessage: string;
}

/**
 * Shared feature-gate check for org-scoped pages: asks the backend whether the
 * currently selected org may access `pageName` (PageName enum + its
 * FeatureKeys). The check only applies for SYSTEM/PARTNER logins browsing with
 * an org selected — NORMAL logins and the "All" scope always resolve to
 * accessible. Presentation is up to the caller (banner, empty state, ...).
 */
const usePagePermissionCheck = (
  pageName?: string,
  onAccessResolved?: (hasAccess: boolean) => void
): PagePermissionCheck => {
  const { scopeOrgId } = useOrg();
  const loginInfo = useSelector((state: RootState) => state.user);

  const [orgName, setOrgName] = useState<string>();
  const [requiredFeatures, setRequiredFeatures] = useState<string[]>([]);
  const [currentPlan, setCurrentPlan] = useState<string>();
  const [hasAccess, setHasAccess] = useState<boolean>(true);

  useEffect(() => {
    if (
      pageName &&
      loginInfo.organisation?.organisationType !== "NORMAL" &&
      scopeOrgId != null &&
      scopeOrgId !== 0
    ) {
      organizationClient
        .checkPagePermission({
          orgId: scopeOrgId,
          pageName: pageName,
        })
        .then(({ data }: { data: CheckPagePermissionResponse }) => {
          if (data.orgName) {
            setOrgName(data.orgName);
            setRequiredFeatures(
              data.requiredFeatures ? [data.requiredFeatures] : []
            );
            setCurrentPlan(data.currentPlan);
            setHasAccess(false);
            onAccessResolved?.(false);
          } else {
            setHasAccess(true);
            onAccessResolved?.(true);
          }
        })
        .catch(() => {});
    } else {
      // No check applies (own org, or no org selected) — clear a stale result
      setHasAccess(true);
      onAccessResolved?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeOrgId]);

  const noAccessMessage = hasAccess
    ? ""
    : currentPlan
      ? StringUtils.replaceStr(
          translate(LanguageKey.org_plan_excludes_feature_key),
          orgName ?? "",
          currentPlan,
          requiredFeatures.join(", ")
        )
      : StringUtils.replaceStr(
          translate(LanguageKey.org_no_access_to_feature_key),
          orgName ?? "",
          requiredFeatures.join(", ")
        );

  return { hasAccess, orgName, requiredFeatures, currentPlan, scopeOrgId, noAccessMessage };
};

export default usePagePermissionCheck;
